<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import GwIcon from '@/components/GwIcon.vue'
import SEOHead from '@/components/SEOHead.vue'
import { useWebTranslations } from '@/composables/useWebTranslations.js'

const { get, locale, webRoute, alternateLocales } = useWebTranslations()

const videoRef = ref(null)
const hasStarted = ref(false)

const videoSrc = '/videos/growiyo-official-demo.mp4'
const posterSrc = '/videos/growiyo-official-demo-poster.jpg'

const contactUrl = computed(() => `${webRoute('web.home')}#contact`)
const canonicalPath = computed(() => webRoute('web.demo'))
const pageAlternateLocales = computed(() => alternateLocales('web.demo'))

const playVideo = async () => {
  const el = videoRef.value
  if (!el) return

  try {
    await el.play()
    hasStarted.value = true
  } catch {
    // Autoplay may be blocked; overlay remains until the user clicks play.
  }
}

const onPlay = () => {
  hasStarted.value = true
}

onMounted(() => {
  playVideo()
})
</script>

<template>
  <SEOHead
    :title="get('static.demo.seo_title')"
    :description="get('static.demo.seo_description')"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <section class="relative isolate overflow-hidden bg-[#0F0B14] min-h-[calc(100vh-5rem)]">
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none overflow-hidden">
      <div
        class="absolute -top-32 -right-24 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-40"
        style="background: radial-gradient(circle, rgba(241,0,254,0.22) 0%, transparent 70%);"
      ></div>
      <div
        class="absolute -bottom-40 -left-28 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-35"
        style="background: radial-gradient(circle, rgba(245,151,84,0.22) 0%, transparent 70%);"
      ></div>
    </div>

    <div class="relative max-w-5xl mx-auto px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
      <div class="text-center mb-10 lg:mb-12">
        <p class="text-growiyo-pink uppercase tracking-[0.2em] text-sm font-semibold mb-4">
          {{ get('static.demo.eyebrow') }}
        </p>
        <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-[-0.02em]">
          {{ get('static.demo.title') }}
        </h1>
        <p class="mt-4 text-white/55 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          {{ get('static.demo.subtitle') }}
        </p>
      </div>

      <div class="relative aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.65)]">
        <video
          ref="videoRef"
          class="absolute inset-0 w-full h-full object-contain bg-black"
          :src="videoSrc"
          :poster="posterSrc"
          controls
          playsinline
          preload="metadata"
          :aria-label="get('static.demo.video_aria')"
          @play="onPlay"
        />

        <button
          v-if="!hasStarted"
          type="button"
          class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-black/35 hover:bg-black/25 transition-colors"
          :aria-label="get('static.demo.play')"
          @click="playVideo"
        >
          <span
            class="w-20 h-20 lg:w-24 lg:h-24 rounded-full growiyo-gradient-bg flex items-center justify-center shadow-lg"
          >
            <GwIcon name="play" class="w-8 h-8 lg:w-10 lg:h-10 text-growiyo-dark ml-1" />
          </span>
          <span class="text-white/70 text-sm font-medium">{{ get('static.demo.play') }}</span>
        </button>
      </div>

      <div class="mt-12 text-center">
        <a
          :href="contactUrl"
          class="gw-btn-grad touch-manipulation inline-flex justify-center"
        >
          {{ get('static.demo.cta') }}
        </a>
        <p class="mt-4 text-white/40 text-sm">
          {{ get('static.demo.cta_note') }}
        </p>
      </div>
    </div>
  </section>
</template>
